import { c as _c } from "react/compiler-runtime";
import { IcoArrowRightSmall16, IcoArrowTopRight16, IcoCheckCircle16, IcoChevronRight16, IcoClose16, IcoCode16, IcoEye16, IcoFileText16, IcoGlobe16, IcoLayer0116, IcoPaint16, IcoPencil16, IcoSearch16, IcoShield16, IcoSparkles16, IcoUnorderedList16, IcoUpload16, IcoWarning16 } from '@onefootprint/icons';
import type { ChatSessionResponse } from '@onefootprint/request-types/dashboard';
import { Button, LoadingSpinner, resolveCodeLanguage, SplitButton } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import Link from 'next/link';
import { createContext, type ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useMobileLayout from '@/hooks/use-mobile-layout';
import useSession from '@/hooks/use-session';
import { getFileTypeLabel } from '@/utils/file-type-labels';
import { useGetFileContent } from '../../hooks/use-chat-files';
import useChatWidgetOpen from '../../hooks/use-chat-widget-open';
import type { ApprovalRequiredMessage, ChatMessage as ChatMessageData, PendingMessage, SendMessageArgs, TaskProgressData, UserMessage } from '../../hooks/use-chat-ws';
import useResourceContext from '../../hooks/use-resource-context';
import type { ResourceContext } from '../../types';
import { isAgentBrowserCommand } from '../../utils/chat-segments';
import { isClaudeSlashCommandLeak } from '../../utils/claude-slash-leak';
import { languageFromPath } from '../../utils/detect-language';
import { formatThinkingDuration } from '../../utils/format-thinking-duration';
import { hostOf } from '../../utils/host-of';
import { prettifySubagentType } from '../../utils/journey-attribution';
import { presentResource, resourceKey, toResourceList } from '../../utils/resource-presentation';
import { getSopToolMetadata, isSopMcpToolName, type SopMcpToolName } from '../../utils/sop-tools';
import { HLJS_TOKEN_STYLE, SyntaxHighlighter } from '../../utils/syntax-highlighter';
import { isCreateInvestigationTool, isGetAgentRunTool, isResourceCreationTool } from '../../utils/tool-names';
import { extractToolResultText } from '../../utils/tool-result';
import AskUserQuestionFlow, { type AskUserQuestionInput } from '../ask-user-question-flow';
import ChatMarkdown from '../chat-markdown';
import FilePreviewDialog from '../file-preview-dialog';
import IdBadge, { MessageMentionsProvider } from '../id-badge';
import MessageActions, { useHoverState } from '../message-actions';
import MetaCreateInvestigationCall from '../meta-create-investigation-call';
import MetaDecryptVaultCall from '../meta-decrypt-vault-call';
import MetaUpdateVaultCall from '../meta-update-vault-call';
import PinnedTodoList, { type TodoItemData } from '../pinned-todo-list';
import ResourceCreatedCard from '../resource-created-card';

// True inside a read-only transcript (e.g. the investigation-chat modal): navigation
// references (block cards, resource links) render inert, while in-message expand/
// collapse toggles still work — they're context, not navigation.
export const ChatTranscriptReadOnlyContext = createContext(false);
type ChatMessageProps = {
  message: ChatMessageData | PendingMessage;
  onSendMessage?: (content: string, args: SendMessageArgs) => void;
  questionAnswerMessages?: Record<string, UserMessage>;
  latestUnansweredQuestionId?: string;
  session: ChatSessionResponse;
  toolResult?: string;
  /** True when this tool_use's result came back with `is_error=true`. Forwarded
   * from the parent group (or chat-panel) so the generic inline tool row can
   * render a warning icon + red text instead of the normal "completed" caption. */
  toolResultIsError?: boolean;
  approvalToolResult?: string;
  approvalResolved?: boolean;
  onApproval?: (approvalId: string, approved: boolean, approveAll?: boolean) => void;
  /** Cancel a still-queued pending message by its client-generated id. Only wired for
   * pending bubbles; renders the hover x button when present. */
  onCancelPending?: (id: string) => void;
  /** Switches a tool_use row into the compact one-line `verb detail` layout used inside
   * ToolCallGroup's flat list (no leading icon). Standalone tool_use rows keep their
   * leading icon + two-line label/detail layout. */
  hideIcon?: boolean;
  /** The latest assistant turn pins its action row visible (à la Claude); older turns
   * reveal it on hover. */
  isLatest?: boolean;
  /** question_id → that question's `questions`, so an answer message can render its Q/A
   * bubble (with the original question text) at its own chronological position. */
  questionsById?: Record<string, AskUserQuestionInput[]>;
  /** Latest TodoWrite content. The TodoWrite renders at the *first* anchor position in the
   * stream; subsequent updates are reflected here so the list updates in place. */
  latestTodos?: TodoItemData[];
};
const CAPPED_MAX_HEIGHT_PX = 288;

/** Hold the streaming peek closed until the agent has been thinking this long, so short
 * thoughts don't flash a peek box open and shut. The header ("Thinking for Ns") still shows. */
const REVEAL_PEEK_AFTER_MS = 3000;

/** Collapsible reasoning disclosure for Claude `thinking` blocks. */
const ThinkingMessage = t0 => {
  const $ = _c(44);
  const {
    message
  } = t0;
  const [manualExpanded, setManualExpanded] = useState(false);
  const inProgress = message.in_progress;
  const [now, setNow] = useState(_temp);
  const {
    started_at: startedAt,
    completed_at: completedAt
  } = message;
  let t1;
  let t2;
  if ($[0] !== inProgress || $[1] !== startedAt) {
    t1 = () => {
      if (!inProgress || !startedAt) {
        return;
      }
      const id = setInterval(() => setNow(Date.now()), 1000);
      return () => clearInterval(id);
    };
    t2 = [inProgress, startedAt];
    $[0] = inProgress;
    $[1] = startedAt;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] !== now || $[5] !== startedAt) {
    const startMs = startedAt ? new Date(startedAt).getTime() : NaN;
    t3 = Number.isNaN(startMs) || now - startMs >= REVEAL_PEEK_AFTER_MS;
    $[4] = now;
    $[5] = startedAt;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const peekRevealed = t3;
  const capped = inProgress && !manualExpanded && peekRevealed;
  const contentRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(0);
  let t4;
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      const el = contentRef.current;
      if (!el) {
        return;
      }
      const ro = new ResizeObserver(() => setContentHeight(el.scrollHeight));
      ro.observe(el);
      return () => ro.disconnect();
    };
    t5 = [];
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  const maxHeight = manualExpanded ? contentHeight : capped ? CAPPED_MAX_HEIGHT_PX : 0;
  const isOverflowing = contentHeight > CAPPED_MAX_HEIGHT_PX;
  const growingLive = manualExpanded && inProgress;
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => setManualExpanded(_temp2);
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const toggle = t6;
  let t7;
  if ($[10] !== completedAt || $[11] !== now) {
    t7 = completedAt ?? new Date(now).toISOString();
    $[10] = completedAt;
    $[11] = now;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== startedAt || $[14] !== t7) {
    t8 = formatThinkingDuration(startedAt, t7);
    $[13] = startedAt;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const duration = t8;
  const headerLabel = duration ? inProgress ? `Thinking for ${duration}` : `Thought for ${duration}` : inProgress ? "Thinking..." : "Thoughts";
  const t9 = inProgress && "text-shimmer";
  let t10;
  if ($[16] !== t9) {
    t10 = cx(t9);
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== headerLabel || $[19] !== t10) {
    t11 = <span className={t10}>{headerLabel}</span>;
    $[18] = headerLabel;
    $[19] = t10;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const t12 = manualExpanded && "rotate-90";
  let t13;
  if ($[21] !== t12) {
    t13 = cx("shrink-0 transition-transform", t12);
    $[21] = t12;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== t13) {
    t14 = <IcoChevronRight16 className={t13} color="tertiary" />;
    $[23] = t13;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== manualExpanded || $[26] !== t11 || $[27] !== t14) {
    t15 = <button type="button" onClick={toggle} aria-expanded={manualExpanded} className="flex items-center gap-1 text-caption-1 text-tertiary hover:text-secondary transition-colors">{t11}{t14}</button>;
    $[25] = manualExpanded;
    $[26] = t11;
    $[27] = t14;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== maxHeight) {
    t16 = {
      maxHeight
    };
    $[29] = maxHeight;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  const t17 = growingLive ? "transition-[background-color]" : "transition-[max-height,background-color]";
  const t18 = capped && isOverflowing && "[mask-image:linear-gradient(to_bottom,transparent,black_2rem)]";
  let t19;
  if ($[31] !== t17 || $[32] !== t18) {
    t19 = cx("flex flex-col justify-end w-full -mx-2 overflow-hidden rounded-md cursor-pointer duration-300 ease-in-out hover:bg-hover", t17, t18);
    $[31] = t17;
    $[32] = t18;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  let t20;
  if ($[34] !== inProgress || $[35] !== message.content) {
    t20 = <div ref={contentRef} className="px-2 py-1 text-left text-tertiary text-caption-4 font-light [&_*]:text-tertiary [&_*]:font-light"><ChatMarkdown content={message.content} animate={inProgress} /></div>;
    $[34] = inProgress;
    $[35] = message.content;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] !== t16 || $[38] !== t19 || $[39] !== t20) {
    t21 = <button type="button" tabIndex={-1} onClick={toggle} style={t16} className={t19}>{t20}</button>;
    $[37] = t16;
    $[38] = t19;
    $[39] = t20;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] !== t15 || $[42] !== t21) {
    t22 = <div className="flex flex-col items-start gap-1 w-full">{t15}{t21}</div>;
    $[41] = t15;
    $[42] = t21;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  return t22;
};
const ChatMessage = t0 => {
  const $ = _c(200);
  const {
    message,
    onSendMessage,
    questionAnswerMessages,
    latestUnansweredQuestionId,
    session,
    toolResult,
    toolResultIsError,
    approvalToolResult,
    approvalResolved,
    onApproval,
    onCancelPending,
    hideIcon,
    isLatest,
    questionsById,
    latestTodos
  } = t0;
  const {
    t
  } = useTranslation("copilot");
  const readOnly = useContext(ChatTranscriptReadOnlyContext);
  const {
    hovered,
    handlers: hoverBind
  } = useHoverState();
  const currentResource = useResourceContext();
  if ((message.type === "user" || message.type === "pending") && message.question_id && (questionsById?.[message.question_id]?.length ?? 0) > 0) {
    let t1;
    if ($[0] !== message.question_id || $[1] !== questionsById) {
      t1 = questionsById?.[message.question_id] ?? [];
      $[0] = message.question_id;
      $[1] = questionsById;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    const linkedQuestions = t1;
    const linkedHasOptions = linkedQuestions.some(_temp3);
    if (linkedHasOptions) {
      const asAnswer = message as unknown as UserMessage;
      let t2;
      if ($[3] !== asAnswer || $[4] !== message.question_id) {
        t2 = {
          [message.question_id]: asAnswer
        };
        $[3] = asAnswer;
        $[4] = message.question_id;
        $[5] = t2;
      } else {
        t2 = $[5];
      }
      let t3;
      if ($[6] !== linkedQuestions || $[7] !== message.question_id || $[8] !== t2) {
        t3 = <AskUserQuestionFlow questions={linkedQuestions} questionId={message.question_id} questionAnswerMessages={t2} disabled={true} />;
        $[6] = linkedQuestions;
        $[7] = message.question_id;
        $[8] = t2;
        $[9] = t3;
      } else {
        t3 = $[9];
      }
      return t3;
    }
  }
  if (message.type === "pending" || message.type === "user") {
    let canShowActions;
    let mentions;
    let t1;
    let t2;
    let t3;
    let t4;
    let t5;
    let t6;
    let t7;
    if ($[10] !== currentResource || $[11] !== hoverBind || $[12] !== hovered || $[13] !== message.context || $[14] !== message.file_names || $[15] !== message.id || $[16] !== message.mentions || $[17] !== message.question_id || $[18] !== message.type || $[19] !== onCancelPending || $[20] !== readOnly || $[21] !== session || $[22] !== t) {
      let t8;
      if ($[32] !== currentResource || $[33] !== t) {
        t8 = c => {
          const presented = presentResource(c, t);
          return presented ? [{
            key: resourceKey(c),
            presented,
            isOnResourcePage: isSameResource(c, currentResource)
          }] : [];
        };
        $[32] = currentResource;
        $[33] = t;
        $[34] = t8;
      } else {
        t8 = $[34];
      }
      const resources = toResourceList(message.context).flatMap(t8);
      let t9;
      if ($[35] !== message.mentions) {
        t9 = message.mentions ?? [];
        $[35] = message.mentions;
        $[36] = t9;
      } else {
        t9 = $[36];
      }
      mentions = t9;
      canShowActions = !message.question_id && message.type !== "pending";
      t6 = "flex flex-col items-end gap-1";
      t7 = hoverBind;
      t5 = "flex flex-col items-end gap-3 w-full";
      const t10 = message.type === "pending" ? "opacity-60" : "";
      if ($[37] !== t10) {
        t1 = cx("relative bg-secondary rounded-2xl px-3 py-2 max-w-[80%]", t10);
        $[37] = t10;
        $[38] = t1;
      } else {
        t1 = $[38];
      }
      if ($[39] !== hovered || $[40] !== message.id || $[41] !== message.type || $[42] !== onCancelPending) {
        t2 = message.type === "pending" && message.id && onCancelPending && <button type="button" aria-label="Cancel message" onClick={() => onCancelPending(message.id as string)} className={cx("absolute -right-2.5 -top-2.5 p-1 text-tertiary hover:text-primary cursor-pointer bg-primary border border-tertiary rounded-full transition-opacity transition-colors", hovered ? "opacity-100" : "opacity-0 max-md:opacity-100")}><IcoClose16 /></button>;
        $[39] = hovered;
        $[40] = message.id;
        $[41] = message.type;
        $[42] = onCancelPending;
        $[43] = t2;
      } else {
        t2 = $[43];
      }
      if ($[44] !== message.file_names || $[45] !== message.type || $[46] !== session) {
        t3 = message.file_names && message.file_names.length > 0 && <div className="flex flex-wrap gap-1.5 mb-2">{message.file_names.map(fileName => <FileAttachment key={fileName} session={session} fileName={fileName} disabled={message.type === "pending"} />)}</div>;
        $[44] = message.file_names;
        $[45] = message.type;
        $[46] = session;
        $[47] = t3;
      } else {
        t3 = $[47];
      }
      let t11;
      if ($[48] !== message.type || $[49] !== readOnly) {
        t11 = t12 => {
          const {
            key,
            presented: presented_0,
            isOnResourcePage
          } = t12;
          return isOnResourcePage || readOnly ? <span key={key} data-resource-key={key} className={cx(["flex w-fit max-w-full items-center gap-1 bg-primary/80 border border-tertiary rounded px-3 py-1 mb-2", message.type === "pending" ? "opacity-80" : ""])}><span className="shrink-0">{presented_0.icon}</span><span className="text-caption-3 font-medium text-primary truncate" title={presented_0.name ?? undefined}>{presented_0.name ?? `This ${presented_0.label.toLowerCase()}`}</span>{presented_0.subtitle && <><span aria-hidden={true} className="shrink-0 text-quaternary">/</span><span className="text-caption-3 font-medium text-primary truncate" title={presented_0.subtitle}>{presented_0.subtitle}</span></>}</span> : <Link key={key} data-resource-key={key} href={presented_0.href} className={cx(["flex w-fit max-w-full items-center gap-1 bg-primary/80 border border-tertiary rounded px-3 py-1 mb-2 cursor-pointer hover:opacity-70 transition-opacity", message.type === "pending" ? "opacity-80 cursor-default" : ""])}><span className="shrink-0">{presented_0.icon}</span><span className="text-caption-3 font-medium text-primary truncate" title={presented_0.name ?? undefined}>{presented_0.name ?? presented_0.label}</span>{presented_0.subtitle && <><span aria-hidden={true} className="shrink-0 text-quaternary">/</span><span className="text-caption-3 font-medium text-primary truncate" title={presented_0.subtitle}>{presented_0.subtitle}</span></>}<span className="shrink-0"><IcoArrowTopRight16 /></span></Link>;
        };
        $[48] = message.type;
        $[49] = readOnly;
        $[50] = t11;
      } else {
        t11 = $[50];
      }
      t4 = resources.map(t11);
      $[10] = currentResource;
      $[11] = hoverBind;
      $[12] = hovered;
      $[13] = message.context;
      $[14] = message.file_names;
      $[15] = message.id;
      $[16] = message.mentions;
      $[17] = message.question_id;
      $[18] = message.type;
      $[19] = onCancelPending;
      $[20] = readOnly;
      $[21] = session;
      $[22] = t;
      $[23] = canShowActions;
      $[24] = mentions;
      $[25] = t1;
      $[26] = t2;
      $[27] = t3;
      $[28] = t4;
      $[29] = t5;
      $[30] = t6;
      $[31] = t7;
    } else {
      canShowActions = $[23];
      mentions = $[24];
      t1 = $[25];
      t2 = $[26];
      t3 = $[27];
      t4 = $[28];
      t5 = $[29];
      t6 = $[30];
      t7 = $[31];
    }
    let t8;
    if ($[51] !== message.content) {
      t8 = <ChatMarkdown content={message.content} />;
      $[51] = message.content;
      $[52] = t8;
    } else {
      t8 = $[52];
    }
    let t9;
    if ($[53] !== mentions || $[54] !== t8) {
      t9 = <MessageMentionsProvider mentions={mentions}>{t8}</MessageMentionsProvider>;
      $[53] = mentions;
      $[54] = t8;
      $[55] = t9;
    } else {
      t9 = $[55];
    }
    let t10;
    if ($[56] !== t1 || $[57] !== t2 || $[58] !== t3 || $[59] !== t4 || $[60] !== t9) {
      t10 = <div className={t1}>{t2}{t3}{t4}{t9}</div>;
      $[56] = t1;
      $[57] = t2;
      $[58] = t3;
      $[59] = t4;
      $[60] = t9;
      $[61] = t10;
    } else {
      t10 = $[61];
    }
    let t11;
    if ($[62] !== t10 || $[63] !== t5) {
      t11 = <div className={t5}>{t10}</div>;
      $[62] = t10;
      $[63] = t5;
      $[64] = t11;
    } else {
      t11 = $[64];
    }
    let t12;
    if ($[65] !== canShowActions || $[66] !== hovered || $[67] !== message.content || $[68] !== message.timestamp) {
      t12 = canShowActions && <MessageActions content={message.content} timestamp={message.timestamp} visible={hovered} />;
      $[65] = canShowActions;
      $[66] = hovered;
      $[67] = message.content;
      $[68] = message.timestamp;
      $[69] = t12;
    } else {
      t12 = $[69];
    }
    let t13;
    if ($[70] !== t11 || $[71] !== t12 || $[72] !== t6 || $[73] !== t7) {
      t13 = <div className={t6} {...t7}>{t11}{t12}</div>;
      $[70] = t11;
      $[71] = t12;
      $[72] = t6;
      $[73] = t7;
      $[74] = t13;
    } else {
      t13 = $[74];
    }
    return t13;
  }
  if (message.type === "thinking") {
    if (!message.content.trim()) {
      return null;
    }
    let t1;
    if ($[75] !== message) {
      t1 = <ThinkingMessage message={message} />;
      $[75] = message;
      $[76] = t1;
    } else {
      t1 = $[76];
    }
    return t1;
  }
  if (message.type === "text") {
    if (!message.content.trim()) {
      return null;
    }
    if (isClaudeSlashCommandLeak(message.content)) {
      console.warn("[agent-chat] Suppressed Claude CLI slash-command output leaking into chat.");
      return null;
    }
    let t1;
    if ($[77] !== message.content || $[78] !== message.in_progress) {
      t1 = <ChatMarkdown content={message.content} animate={message.in_progress} />;
      $[77] = message.content;
      $[78] = message.in_progress;
      $[79] = t1;
    } else {
      t1 = $[79];
    }
    let t2;
    if ($[80] !== hovered || $[81] !== isLatest || $[82] !== message.content || $[83] !== message.in_progress || $[84] !== message.timestamp) {
      t2 = !message.in_progress && <MessageActions content={message.content} timestamp={message.timestamp} alwaysVisible={isLatest} visible={hovered} />;
      $[80] = hovered;
      $[81] = isLatest;
      $[82] = message.content;
      $[83] = message.in_progress;
      $[84] = message.timestamp;
      $[85] = t2;
    } else {
      t2 = $[85];
    }
    let t3;
    if ($[86] !== hoverBind || $[87] !== t1 || $[88] !== t2) {
      t3 = <div className="flex flex-col items-start gap-1" {...hoverBind}>{t1}{t2}</div>;
      $[86] = hoverBind;
      $[87] = t1;
      $[88] = t2;
      $[89] = t3;
    } else {
      t3 = $[89];
    }
    return t3;
  }
  if (message.type === "tool_use") {
    if (message.tool === "AskUserQuestion") {
      let answer;
      let questions;
      let t1;
      if ($[90] !== hoverBind || $[91] !== hovered || $[92] !== isLatest || $[93] !== message.id || $[94] !== message.input.questions || $[95] !== message.timestamp || $[96] !== questionAnswerMessages) {
        t1 = Symbol.for("react.early_return_sentinel");
        bb0: {
          questions = message.input.questions as AskUserQuestionInput[] ?? [];
          const hasOptions = questions.some(_temp4);
          answer = questionAnswerMessages?.[message.id];
          if (!hasOptions) {
            if (answer) {
              t1 = null;
              break bb0;
            }
            const text = questions.map(_temp5).join("\n\n");
            let t2;
            if ($[100] !== text) {
              t2 = <ChatMarkdown content={text} />;
              $[100] = text;
              $[101] = t2;
            } else {
              t2 = $[101];
            }
            let t3;
            if ($[102] !== hovered || $[103] !== isLatest || $[104] !== message.timestamp || $[105] !== text) {
              t3 = <MessageActions content={text} timestamp={message.timestamp} alwaysVisible={isLatest} visible={hovered} />;
              $[102] = hovered;
              $[103] = isLatest;
              $[104] = message.timestamp;
              $[105] = text;
              $[106] = t3;
            } else {
              t3 = $[106];
            }
            let t4;
            if ($[107] !== hoverBind || $[108] !== t2 || $[109] !== t3) {
              t4 = <div className="flex flex-col items-start gap-1" {...hoverBind}>{t2}{t3}</div>;
              $[107] = hoverBind;
              $[108] = t2;
              $[109] = t3;
              $[110] = t4;
            } else {
              t4 = $[110];
            }
            t1 = t4;
            break bb0;
          }
        }
        $[90] = hoverBind;
        $[91] = hovered;
        $[92] = isLatest;
        $[93] = message.id;
        $[94] = message.input.questions;
        $[95] = message.timestamp;
        $[96] = questionAnswerMessages;
        $[97] = answer;
        $[98] = questions;
        $[99] = t1;
      } else {
        answer = $[97];
        questions = $[98];
        t1 = $[99];
      }
      if (t1 !== Symbol.for("react.early_return_sentinel")) {
        return t1;
      }
      if (answer) {
        let t2;
        if ($[111] !== answer || $[112] !== message.id) {
          t2 = {
            [message.id]: answer
          };
          $[111] = answer;
          $[112] = message.id;
          $[113] = t2;
        } else {
          t2 = $[113];
        }
        let t3;
        if ($[114] !== message.id || $[115] !== questions || $[116] !== t2) {
          t3 = <AskUserQuestionFlow questions={questions} questionId={message.id} questionAnswerMessages={t2} disabled={true} />;
          $[114] = message.id;
          $[115] = questions;
          $[116] = t2;
          $[117] = t3;
        } else {
          t3 = $[117];
        }
        return t3;
      }
      let t2;
      if ($[118] !== questionAnswerMessages) {
        t2 = questionAnswerMessages ?? {};
        $[118] = questionAnswerMessages;
        $[119] = t2;
      } else {
        t2 = $[119];
      }
      const t3 = message.id !== latestUnansweredQuestionId;
      let t4;
      if ($[120] !== message.id || $[121] !== onSendMessage || $[122] !== questions || $[123] !== t2 || $[124] !== t3) {
        t4 = <AskUserQuestionFlow questions={questions} onSendMessage={onSendMessage} questionId={message.id} questionAnswerMessages={t2} disabled={t3} />;
        $[120] = message.id;
        $[121] = onSendMessage;
        $[122] = questions;
        $[123] = t2;
        $[124] = t3;
        $[125] = t4;
      } else {
        t4 = $[125];
      }
      return t4;
    }
    if (message.tool === "TodoWrite") {
      const t1 = latestTodos ?? message.input.todos as TodoItemData[];
      let t2;
      if ($[126] !== t1) {
        t2 = <PinnedTodoList todos={t1} variant="inline" />;
        $[126] = t1;
        $[127] = t2;
      } else {
        t2 = $[127];
      }
      return t2;
    }
    if (message.tool === "mcp__meta__decrypt_entity_vault") {
      let t1;
      if ($[128] !== hideIcon || $[129] !== message.input || $[130] !== toolResult) {
        t1 = <MetaDecryptVaultCall input={message.input} toolResult={toolResult} hideIcon={hideIcon} />;
        $[128] = hideIcon;
        $[129] = message.input;
        $[130] = toolResult;
        $[131] = t1;
      } else {
        t1 = $[131];
      }
      return t1;
    }
    if (message.tool === "mcp__meta__update_entity_vault") {
      let t1;
      if ($[132] !== hideIcon || $[133] !== message.input || $[134] !== toolResult) {
        t1 = <MetaUpdateVaultCall input={message.input} toolResult={toolResult} hideIcon={hideIcon} />;
        $[132] = hideIcon;
        $[133] = message.input;
        $[134] = toolResult;
        $[135] = t1;
      } else {
        t1 = $[135];
      }
      return t1;
    }
    if (isCreateInvestigationTool(message.tool)) {
      const t1 = !!toolResultIsError;
      let t2;
      if ($[136] !== hideIcon || $[137] !== message.input || $[138] !== t1 || $[139] !== toolResult) {
        t2 = <MetaCreateInvestigationCall input={message.input} toolResult={toolResult} toolResultIsError={t1} hideIcon={hideIcon} />;
        $[136] = hideIcon;
        $[137] = message.input;
        $[138] = t1;
        $[139] = toolResult;
        $[140] = t2;
      } else {
        t2 = $[140];
      }
      return t2;
    }
    if (isSopMcpToolName(message.tool)) {
      let t1;
      if ($[141] !== hideIcon || $[142] !== message.input || $[143] !== message.tool || $[144] !== toolResult) {
        t1 = <SopToolCall tool={message.tool} input={message.input} toolResult={toolResult} hideIcon={hideIcon} />;
        $[141] = hideIcon;
        $[142] = message.input;
        $[143] = message.tool;
        $[144] = toolResult;
        $[145] = t1;
      } else {
        t1 = $[145];
      }
      return t1;
    }
    let t1;
    if ($[146] !== message.input || $[147] !== message.tool) {
      t1 = getToolDisplay(message.tool, message.input);
      $[146] = message.input;
      $[147] = message.tool;
      $[148] = t1;
    } else {
      t1 = $[148];
    }
    const info = t1;
    if (!info) {
      return null;
    }
    const inProgress = !toolResult;
    if (hideIcon) {
      const t2 = !!toolResultIsError;
      let t3;
      if ($[149] !== info.detail || $[150] !== info.label || $[151] !== message.input || $[152] !== message.tool || $[153] !== t2 || $[154] !== toolResult) {
        t3 = <CompactToolRow tool={message.tool} input={message.input} label={info.label} detail={info.detail} toolResult={toolResult} toolResultIsError={t2} />;
        $[149] = info.detail;
        $[150] = info.label;
        $[151] = message.input;
        $[152] = message.tool;
        $[153] = t2;
        $[154] = toolResult;
        $[155] = t3;
      } else {
        t3 = $[155];
      }
      return t3;
    }
    const t2 = toolResultIsError ? "text-error" : "text-tertiary";
    let t3;
    if ($[156] !== t2) {
      t3 = cx("flex items-start gap-1.5 text-caption-1", t2);
      $[156] = t2;
      $[157] = t3;
    } else {
      t3 = $[157];
    }
    let t4;
    if ($[158] !== info.icon || $[159] !== toolResultIsError) {
      t4 = toolResultIsError ? <IcoWarning16 color="error" /> : info.icon;
      $[158] = info.icon;
      $[159] = toolResultIsError;
      $[160] = t4;
    } else {
      t4 = $[160];
    }
    let t5;
    if ($[161] !== t4) {
      t5 = <span className="shrink-0 flex items-center h-[1.25em]">{t4}</span>;
      $[161] = t4;
      $[162] = t5;
    } else {
      t5 = $[162];
    }
    const t6 = toolResultIsError ? "text-error font-medium" : "text-secondary";
    const t7 = inProgress && "text-shimmer";
    let t8;
    if ($[163] !== t6 || $[164] !== t7) {
      t8 = cx(t6, t7);
      $[163] = t6;
      $[164] = t7;
      $[165] = t8;
    } else {
      t8 = $[165];
    }
    const t9 = toolResultIsError && " \u2014 failed";
    let t10;
    if ($[166] !== info.label || $[167] !== t8 || $[168] !== t9) {
      t10 = <span className={t8}>{info.label}{t9}</span>;
      $[166] = info.label;
      $[167] = t8;
      $[168] = t9;
      $[169] = t10;
    } else {
      t10 = $[169];
    }
    let t11;
    if ($[170] !== info.detail) {
      t11 = info.detail && <span className="font-mono text-caption-2 truncate">{info.detail}</span>;
      $[170] = info.detail;
      $[171] = t11;
    } else {
      t11 = $[171];
    }
    let t12;
    if ($[172] !== t10 || $[173] !== t11) {
      t12 = <div className="flex flex-col gap-1 min-w-0">{t10}{t11}</div>;
      $[172] = t10;
      $[173] = t11;
      $[174] = t12;
    } else {
      t12 = $[174];
    }
    let t13;
    if ($[175] !== t12 || $[176] !== t3 || $[177] !== t5) {
      t13 = <div className={t3}>{t5}{t12}</div>;
      $[175] = t12;
      $[176] = t3;
      $[177] = t5;
      $[178] = t13;
    } else {
      t13 = $[178];
    }
    return t13;
  }
  if (message.type === "resource" && message.resource.kind === "browser_session") {
    let t1;
    if ($[179] !== message) {
      t1 = <ResourceCreatedCard message={message} />;
      $[179] = message;
      $[180] = t1;
    } else {
      t1 = $[180];
    }
    return t1;
  }
  if (message.type === "resource" && message.resource.kind === "ui_block") {
    const t1 = message.kind === "update";
    let t2;
    if ($[181] !== message.resource.key || $[182] !== message.resource.name || $[183] !== t1) {
      t2 = <GenUiBlockToolCall resourceKey={message.resource.key} title={message.resource.name} isUpdate={t1} />;
      $[181] = message.resource.key;
      $[182] = message.resource.name;
      $[183] = t1;
      $[184] = t2;
    } else {
      t2 = $[184];
    }
    return t2;
  }
  if (message.type === "resource" && message.kind === "create") {
    let t1;
    if ($[185] !== message) {
      t1 = <ResourceCreatedCard message={message} />;
      $[185] = message;
      $[186] = t1;
    } else {
      t1 = $[186];
    }
    return t1;
  }
  if (message.type === "task_progress") {
    let t1;
    if ($[187] !== hideIcon || $[188] !== message.data) {
      t1 = <TaskProgress data={message.data} hideIcon={hideIcon} />;
      $[187] = hideIcon;
      $[188] = message.data;
      $[189] = t1;
    } else {
      t1 = $[189];
    }
    return t1;
  }
  if (message.type === "approval_required") {
    let t1;
    if ($[190] !== approvalResolved || $[191] !== approvalToolResult || $[192] !== message || $[193] !== onApproval) {
      t1 = <ApprovalCard message={message} onApprove={onApproval} toolResult={approvalToolResult} resolved={approvalResolved} />;
      $[190] = approvalResolved;
      $[191] = approvalToolResult;
      $[192] = message;
      $[193] = onApproval;
      $[194] = t1;
    } else {
      t1 = $[194];
    }
    return t1;
  }
  if (message.type === "tool_result" || message.type === "turn_complete") {
    return null;
  }
  if (message.type === "error") {
    let t1;
    if ($[195] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <IcoWarning16 className="shrink-0 text-error mt-px" />;
      $[195] = t1;
    } else {
      t1 = $[195];
    }
    let t2;
    if ($[196] !== message.message) {
      t2 = <div role="alert" className="flex items-start gap-2 bg-error/10 rounded-lg px-3 py-2">{t1}<p className="text-body-2 text-error max-md:min-w-0 max-md:break-words">{message.message}</p></div>;
      $[196] = message.message;
      $[197] = t2;
    } else {
      t2 = $[197];
    }
    return t2;
  }
  if (message.type === "system") {
    let t1;
    if ($[198] !== message.content) {
      t1 = <div className="text-caption-2 text-tertiary py-1 text-center italic">{message.content}</div>;
      $[198] = message.content;
      $[199] = t1;
    } else {
      t1 = $[199];
    }
    return t1;
  }
  return null;
};

// Tools whose `toolResult` carries content worth showing in an expanded row
// (file body, command output, search matches, query payload). Tools whose
// result is just an ack (`create_entity`, `run_agent`, etc.) are left
// un-expandable — the label already says everything useful.
const EXPANDABLE_TOOLS = new Set<string>(['mcp__workspace__read', 'mcp__workspace__bash', 'Bash', 'mcp__workspace__grep', 'mcp__workspace__glob', 'mcp__workspace__write', 'Write', 'mcp__workspace__edit', 'Edit', 'WebSearch', 'WebFetch', 'mcp__meta__entities_query', 'mcp__meta__list_entity_vault']);
type CompactToolRowProps = {
  tool: string;
  input: Record<string, unknown>;
  label: string;
  detail: ReactNode | undefined;
  toolResult: string | undefined;
  toolResultIsError: boolean;
};
const CompactToolRow = t0 => {
  const $ = _c(48);
  const {
    tool,
    input,
    label,
    detail,
    toolResult,
    toolResultIsError
  } = t0;
  const [expanded, setExpanded] = useState(false);
  const inProgress = !toolResult;
  let t1;
  if ($[0] !== inProgress || $[1] !== tool) {
    t1 = !inProgress && EXPANDABLE_TOOLS.has(tool);
    $[0] = inProgress;
    $[1] = tool;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const expandable = t1;
  let t2;
  if ($[3] !== toolResultIsError) {
    t2 = toolResultIsError && <span className="shrink-0 flex items-center self-center h-[1.25em]"><IcoWarning16 color="error" /></span>;
    $[3] = toolResultIsError;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = toolResultIsError ? "text-error font-medium" : "text-tertiary";
  const t4 = inProgress && "text-shimmer";
  let t5;
  if ($[5] !== t3 || $[6] !== t4) {
    t5 = cx("shrink-0", t3, t4);
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = toolResultIsError && " \u2014 failed";
  let t7;
  if ($[8] !== label || $[9] !== t5 || $[10] !== t6) {
    t7 = <span className={t5}>{label}{t6}</span>;
    $[8] = label;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== detail) {
    t8 = detail && <span className="font-mono text-secondary truncate min-w-0">{detail}</span>;
    $[12] = detail;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== expandable || $[15] !== expanded) {
    t9 = expandable && <span className="shrink-0 flex items-center self-center h-[1.25em]"><IcoChevronRight16 className={cx("text-quaternary transition-transform", expanded && "rotate-90")} /></span>;
    $[14] = expandable;
    $[15] = expanded;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t2 || $[18] !== t7 || $[19] !== t8 || $[20] !== t9) {
    t10 = <>{t2}{t7}{t8}{t9}</>;
    $[17] = t2;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const rowInner = t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  if ($[22] !== expandable || $[23] !== expanded || $[24] !== rowInner || $[25] !== toolResultIsError) {
    t15 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const rowClass = cx("flex items-baseline gap-1.5 text-caption-1 min-w-0", toolResultIsError ? "text-error" : "text-tertiary");
      if (!expandable) {
        t15 = <div className={rowClass}>{rowInner}</div>;
        break bb0;
      }
      const t16 = expanded && "pb-1";
      if ($[31] !== t16) {
        t14 = cx("flex flex-col gap-1.5 min-w-0", t16);
        $[31] = t16;
        $[32] = t14;
      } else {
        t14 = $[32];
      }
      t11 = "button";
      if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
        t12 = () => setExpanded(_temp6);
        $[33] = t12;
      } else {
        t12 = $[33];
      }
      t13 = cx(rowClass, "w-full text-left cursor-pointer hover:text-secondary transition-colors");
    }
    $[22] = expandable;
    $[23] = expanded;
    $[24] = rowInner;
    $[25] = toolResultIsError;
    $[26] = t11;
    $[27] = t12;
    $[28] = t13;
    $[29] = t14;
    $[30] = t15;
  } else {
    t11 = $[26];
    t12 = $[27];
    t13 = $[28];
    t14 = $[29];
    t15 = $[30];
  }
  if (t15 !== Symbol.for("react.early_return_sentinel")) {
    return t15;
  }
  let t16;
  if ($[34] !== rowInner || $[35] !== t11 || $[36] !== t12 || $[37] !== t13) {
    t16 = <button type={t11} onClick={t12} className={t13}>{rowInner}</button>;
    $[34] = rowInner;
    $[35] = t11;
    $[36] = t12;
    $[37] = t13;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== expanded || $[40] !== input || $[41] !== tool || $[42] !== toolResult) {
    t17 = expanded && toolResult && <ExpandedToolResult tool={tool} input={input} toolResult={toolResult} />;
    $[39] = expanded;
    $[40] = input;
    $[41] = tool;
    $[42] = toolResult;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] !== t14 || $[45] !== t16 || $[46] !== t17) {
    t18 = <div className={t14}>{t16}{t17}</div>;
    $[44] = t14;
    $[45] = t16;
    $[46] = t17;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  return t18;
};

/** Pick a highlight.js language slug for an expanded tool result, or null to
 *  render as plain mono. Tool-specific overrides take priority over the file-
 *  extension map, which takes priority over a JSON-shape sniff. */
const detectLanguage = (tool: string, input: Record<string, unknown>, text: string): string | null => {
  switch (tool) {
    case 'mcp__workspace__bash':
    case 'Bash':
      return 'bash';
    case 'mcp__meta__entities_query':
    case 'mcp__meta__list_entity_vault':
      return 'json';
    case 'WebSearch':
    case 'WebFetch':
      return null;
  }
  const fromPath = languageFromPath(input.file_path as string | undefined ?? input.path as string | undefined);
  if (fromPath) return fromPath;
  const trimmed = text.trim();
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      JSON.parse(trimmed);
      return 'json';
    } catch {
      // not JSON; fall through
    }
  }
  return null;
};

// `mcp__workspace__read` prepends each line with a right-aligned line-number
// gutter. The exact shape varies (`   1: content`, `   1\tcontent`, or just
// `   1   content`), so match the union: leading whitespace, digits, then a
// colon-or-whitespace separator. Strip only if the majority of non-empty lines
// match — keeps us from corrupting legitimate content that happens to start
// with digits.
const LINE_NUMBER_PREFIX_RE = /^\s*\d+(?::\s?|\s{2,}|\t)/;
const stripReadToolLineNumbers = (text: string): string => {
  const lines = text.split('\n');
  const nonEmpty = lines.filter(l => l.trim()).length;
  const matched = lines.filter(l => LINE_NUMBER_PREFIX_RE.test(l)).length;
  if (nonEmpty === 0 || matched < nonEmpty * 0.6) return text;
  return lines.map(l => l.replace(LINE_NUMBER_PREFIX_RE, '')).join('\n');
};
const MAX_EXPANDED_CHARS = 6000;
const HIGHLIGHTER_STYLE = {
  background: 'transparent',
  fontFamily: 'var(--font-family-code)',
  fontSize: '12px',
  lineHeight: '1.5',
  margin: 0,
  padding: '6px 10px'
};
const HIGHLIGHTER_CODE_TAG_STYLE = {
  style: {
    fontFamily: 'var(--font-family-code)'
  }
};

// Inline expansion. Tight padding so a one-line file read doesn't look like a
// giant empty box, and a 200-line read scrolls inside a fixed-height frame.
const ExpandedToolResult = t0 => {
  const $ = _c(4);
  const {
    tool,
    input,
    toolResult
  } = t0;
  let t1;
  if ($[0] !== input || $[1] !== tool || $[2] !== toolResult) {
    let raw = extractToolResultText(toolResult) ?? toolResult;
    if (tool === "mcp__workspace__read") {
      raw = stripReadToolLineNumbers(raw);
    }
    const text = raw.length > MAX_EXPANDED_CHARS ? `${raw.slice(0, MAX_EXPANDED_CHARS)}\n…` : raw;
    const language = detectLanguage(tool, input, text);
    t1 = <div className="bg-secondary rounded-md overflow-auto max-h-[320px]">{language ? <SyntaxHighlighter language={resolveCodeLanguage(language)} customStyle={HIGHLIGHTER_STYLE} codeTagProps={HIGHLIGHTER_CODE_TAG_STYLE} style={HLJS_TOKEN_STYLE}>{text}</SyntaxHighlighter> : <pre className="font-mono text-[12px] leading-[1.5] text-secondary whitespace-pre-wrap break-words px-[10px] py-[6px]">{text}</pre>}</div>;
    $[0] = input;
    $[1] = tool;
    $[2] = toolResult;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
};
const FileAttachment = t0 => {
  const $ = _c(24);
  const {
    session,
    fileName,
    disabled
  } = t0;
  const [isOpen, setIsOpen] = useState(false);
  const getFileContent = useGetFileContent(session);
  const t1 = disabled ? "opacity-80 cursor-default" : "hover:opacity-70 cursor-pointer";
  let t2;
  if ($[0] !== t1) {
    t2 = cx("inline-flex max-w-full items-center gap-1 min-w-0 bg-primary/80 border border-tertiary rounded px-3 py-1 transition-opacity text-left", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => setIsOpen(true);
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <IcoFileText16 className="shrink-0" />;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== fileName) {
    t5 = <span className="text-caption-3 font-medium text-primary truncate">{fileName}</span>;
    $[4] = fileName;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== fileName) {
    t6 = getFileTypeLabel(fileName);
    $[6] = fileName;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t6) {
    t7 = <span className="sr-only">{t6}</span>;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== disabled || $[11] !== fileName || $[12] !== t2 || $[13] !== t5 || $[14] !== t7) {
    t8 = <button type="button" className={t2} onClick={t3} disabled={disabled} title={fileName}>{t4}{t5}{t7}</button>;
    $[10] = disabled;
    $[11] = fileName;
    $[12] = t2;
    $[13] = t5;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => setIsOpen(false);
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== fileName || $[18] !== getFileContent.mutateAsync || $[19] !== isOpen) {
    t10 = <FilePreviewDialog open={isOpen} onClose={t9} fileName={fileName} getFileContent={getFileContent.mutateAsync} />;
    $[17] = fileName;
    $[18] = getFileContent.mutateAsync;
    $[19] = isOpen;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t8) {
    t11 = <>{t8}{t10}</>;
    $[21] = t10;
    $[22] = t8;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  return t11;
};
const TaskProgress = t0 => {
  const $ = _c(10);
  const {
    data,
    hideIcon
  } = t0;
  let t1;
  if ($[0] !== data.last_tool_name) {
    t1 = getToolDisplay(data.last_tool_name ?? "", {})?.icon;
    $[0] = data.last_tool_name;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const toolIcon = t1;
  let t2;
  if ($[2] !== hideIcon || $[3] !== toolIcon) {
    t2 = !hideIcon && (toolIcon ?? <LoadingSpinner size="default" color="tertiary" className="shrink-0 mt-px" />);
    $[2] = hideIcon;
    $[3] = toolIcon;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== data.description) {
    t3 = <span className="truncate text-shimmer">{data.description}</span>;
    $[5] = data.description;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <div className="flex items-start gap-2 text-caption-1 text-tertiary py-1" aria-live="polite">{t2}{t3}</div>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};
const SCROLL_HIGHLIGHT_MS = 1500;

// Editor block ids are suffixed with the chat key (e.g. `ui_block_abc__<key>`).
// Try exact first, then fall back to suffix match to bridge that rename.
const findUiBlock = (key: string) => document.getElementById(key) ?? document.querySelector<HTMLElement>(`[id$="_${CSS.escape(key)}"]`);
const scrollToUiBlock = (el: HTMLElement) => {
  el.scrollIntoView({
    behavior: 'smooth',
    block: 'start'
  });
  el.classList.add('block-scroll-highlight');
  setTimeout(() => el.classList.remove('block-scroll-highlight'), SCROLL_HIGHLIGHT_MS);
};
const GenUiBlockToolCall = t0 => {
  const $ = _c(23);
  const {
    resourceKey,
    title,
    isUpdate
  } = t0;
  const readOnly = useContext(ChatTranscriptReadOnlyContext);
  const isMobile = useMobileLayout();
  const {
    close
  } = useChatWidgetOpen();
  let t1;
  if ($[0] !== close || $[1] !== isMobile || $[2] !== resourceKey) {
    t1 = () => {
      const block = findUiBlock(resourceKey);
      if (!block) {
        return;
      }
      if (!isMobile) {
        scrollToUiBlock(block);
        return;
      }
      close();
      requestAnimationFrame(() => scrollToUiBlock(block));
    };
    $[0] = close;
    $[1] = isMobile;
    $[2] = resourceKey;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const revealBlock = t1;
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="w-8 h-8 rounded-full bg-neutral-inverted flex items-center justify-center shrink-0"><IcoLayer0116 color="quinary" /></div>;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = title || "UI block";
  let t4;
  if ($[5] !== t3) {
    t4 = <span className="text-label-2 text-primary truncate">{t3}</span>;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = isUpdate ? "Block updated" : "Block added";
  let t6;
  if ($[7] !== t5) {
    t6 = <span className="text-caption-2 text-tertiary">{t5}</span>;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t4 || $[10] !== t6) {
    t7 = <div className="flex flex-col gap-0.5 min-w-0 flex-1">{t4}{t6}</div>;
    $[9] = t4;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== readOnly) {
    t8 = !readOnly && <IcoArrowRightSmall16 className="shrink-0 text-tertiary opacity-0 group-hover:opacity-100 transition-opacity" />;
    $[12] = readOnly;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t7 || $[15] !== t8) {
    t9 = <>{t2}{t7}{t8}</>;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const body = t9;
  if (readOnly) {
    let t10;
    if ($[17] !== body) {
      t10 = <div className="bg-secondary rounded-md p-3 flex items-center gap-3 w-full">{body}</div>;
      $[17] = body;
      $[18] = t10;
    } else {
      t10 = $[18];
    }
    return t10;
  }
  let t10;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = cx("bg-secondary rounded-md p-3 flex items-center gap-3 w-full group", "cursor-pointer hover:bg-hover transition-colors text-left");
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== body || $[21] !== revealBlock) {
    t11 = <button type="button" onClick={revealBlock} className={t10}>{body}</button>;
    $[20] = body;
    $[21] = revealBlock;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  return t11;
};
const _ScreeningToolCall = ({
  input,
  toolResult
}: {
  input: Record<string, unknown>;
  toolResult?: string;
}) => {
  const firstName = input.first_name as string ?? '';
  const lastName = input.last_name as string ?? '';
  const name = [firstName, lastName].filter(Boolean).join(' ') || 'entity';
  if (!toolResult) {
    return <div className="flex items-start gap-2 text-caption-1 text-tertiary py-1">
        <LoadingSpinner size="default" color="tertiary" className="shrink-0 mt-px" />
        <span>
          Screening <span className="font-medium text-secondary">{name}</span>
        </span>
      </div>;
  }
  const text = extractToolResultText(toolResult);
  let isHit = false;
  if (text) {
    try {
      const parsed = JSON.parse(text);
      const signals = parsed.signals ?? parsed.adverse_signals ?? [];
      isHit = Array.isArray(signals) ? signals.length > 0 : Boolean(signals);
    } catch {
      // ignore
    }
  }
  return <div className="flex items-center gap-1 text-caption-2 py-1">
      <IcoCheckCircle16 className="shrink-0 text-primary" />
      <span className="text-secondary">
        Screened <span className="font-medium">{name}</span>
      </span>
      {isHit && <span className="flex items-center gap-1 px-1.5 py-0.5 rounded text-caption-2 font-medium bg-error/10 text-error">
          Hit
        </span>}
    </div>;
};
const SopToolCall = t0 => {
  const $ = _c(43);
  const {
    tool,
    input,
    toolResult,
    hideIcon
  } = t0;
  const [expanded, setExpanded] = useState(false);
  const {
    data: t1
  } = useSession();
  const {
    user
  } = t1;
  let t2;
  if ($[0] !== tool) {
    t2 = getSopToolMetadata(tool);
    $[0] = tool;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const metadata = t2;
  const rawDetail = metadata.detailInputKey ? input[metadata.detailInputKey] : undefined;
  const detail = typeof rawDetail === "string" ? rawDetail : undefined;
  let t3;
  if ($[2] !== toolResult) {
    t3 = toolResult ? extractToolResultText(toolResult) : null;
    $[2] = toolResult;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const markdown = t3;
  const inProgress = !toolResult;
  const canViewBody = !!user?.isFirmEmployee;
  if (inProgress || !canViewBody) {
    let t4;
    if ($[4] !== hideIcon || $[5] !== tool) {
      t4 = !hideIcon && <span className="shrink-0 flex items-center h-[1.25em]">{getSopToolIcon(tool)}</span>;
      $[4] = hideIcon;
      $[5] = tool;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    const t5 = inProgress ? "text-shimmer" : "text-secondary";
    let t6;
    if ($[7] !== metadata.label || $[8] !== t5) {
      t6 = <span className={t5}>{metadata.label}</span>;
      $[7] = metadata.label;
      $[8] = t5;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    let t7;
    if ($[10] !== detail) {
      t7 = detail && <span className="font-mono text-caption-2 truncate">{detail}</span>;
      $[10] = detail;
      $[11] = t7;
    } else {
      t7 = $[11];
    }
    let t8;
    if ($[12] !== t6 || $[13] !== t7) {
      t8 = <div className="flex flex-col gap-0.5 min-w-0">{t6}{t7}</div>;
      $[12] = t6;
      $[13] = t7;
      $[14] = t8;
    } else {
      t8 = $[14];
    }
    let t9;
    if ($[15] !== t4 || $[16] !== t8) {
      t9 = <div className="flex items-start gap-1.5 text-caption-1 text-tertiary">{t4}{t8}</div>;
      $[15] = t4;
      $[16] = t8;
      $[17] = t9;
    } else {
      t9 = $[17];
    }
    return t9;
  }
  let t4;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => setExpanded(_temp7);
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  const t5 = expanded && "rotate-90";
  let t6;
  if ($[19] !== t5) {
    t6 = cx("transition-transform", t5);
    $[19] = t5;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== t6) {
    t7 = <span className="shrink-0 flex items-center h-[1.25em]"><IcoChevronRight16 className={t6} /></span>;
    $[21] = t6;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== hideIcon || $[24] !== tool) {
    t8 = !hideIcon && <span className="shrink-0 flex items-center h-[1.25em]">{getSopToolIcon(tool)}</span>;
    $[23] = hideIcon;
    $[24] = tool;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== metadata.label) {
    t9 = <span className="text-secondary">{metadata.label}</span>;
    $[26] = metadata.label;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== detail) {
    t10 = detail && <span className="font-mono text-caption-2 truncate">{detail}</span>;
    $[28] = detail;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== t10 || $[31] !== t9) {
    t11 = <span className="flex flex-col gap-0.5 min-w-0">{t9}{t10}</span>;
    $[30] = t10;
    $[31] = t9;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== t11 || $[34] !== t7 || $[35] !== t8) {
    t12 = <button type="button" onClick={t4} className="flex items-start gap-1.5 text-caption-1 text-tertiary text-left cursor-pointer">{t7}{t8}{t11}</button>;
    $[33] = t11;
    $[34] = t7;
    $[35] = t8;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== expanded || $[38] !== markdown) {
    t13 = expanded && markdown && <div className="pl-8 text-primary"><ChatMarkdown content={markdown} /></div>;
    $[37] = expanded;
    $[38] = markdown;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  if ($[40] !== t12 || $[41] !== t13) {
    t14 = <div className="flex flex-col gap-2 min-w-0">{t12}{t13}</div>;
    $[40] = t12;
    $[41] = t13;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  return t14;
};
type ApprovalButtonsProps = {
  confirmText: string;
  denyText: string;
  canApproveAll?: boolean;
  onDecide: (approved: boolean, approveAll?: boolean) => void;
};

// Side by side, a phone's half width truncates a long confirm label, so there the buttons stack, confirm on top.
const ApprovalButtons = t0 => {
  const $ = _c(12);
  const {
    confirmText,
    denyText,
    canApproveAll,
    onDecide
  } = t0;
  let t1;
  if ($[0] !== onDecide) {
    t1 = () => onDecide(false, false);
    $[0] = onDecide;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== denyText || $[3] !== t1) {
    t2 = <Button variant="secondary" size="compact" className="max-md:h-10!" onClick={t1}>{denyText}</Button>;
    $[2] = denyText;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== canApproveAll || $[6] !== confirmText || $[7] !== onDecide) {
    t3 = canApproveAll ? <SplitButton variant="primary" size="compact" options={[{
      label: "Approve once",
      value: "once",
      onSelect: () => onDecide(true, false)
    }, {
      label: "Approve all for this session",
      value: "session",
      onSelect: () => onDecide(true, true)
    }]} /> : <Button variant="primary" size="compact" className="max-md:h-10!" onClick={() => onDecide(true, false)}>{confirmText}</Button>;
    $[5] = canApproveAll;
    $[6] = confirmText;
    $[7] = onDecide;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t2 || $[10] !== t3) {
    t4 = <div className="mt-2 flex flex-wrap justify-end gap-2 max-md:flex-col-reverse max-md:items-stretch">{t2}{t3}</div>;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
};
type ApprovalCardProps = {
  message: ApprovalRequiredMessage;
  onApprove?: (approvalId: string, approved: boolean, approveAll?: boolean) => void;
  toolResult?: string;
  resolved?: boolean;
};
const ApprovalCard = t0 => {
  const $ = _c(9);
  const {
    message,
    onApprove,
    toolResult,
    resolved
  } = t0;
  switch (message.kind) {
    case "vendor_tool":
      {
        let t1;
        if ($[0] !== message || $[1] !== onApprove || $[2] !== resolved || $[3] !== toolResult) {
          t1 = <VendorApprovalCard message={message} onApprove={onApprove} toolResult={toolResult} resolved={resolved} />;
          $[0] = message;
          $[1] = onApprove;
          $[2] = resolved;
          $[3] = toolResult;
          $[4] = t1;
        } else {
          t1 = $[4];
        }
        return t1;
      }
    case "browser_takeover":
      {
        return null;
      }
    default:
      {
        let t1;
        if ($[5] !== message || $[6] !== onApprove || $[7] !== resolved) {
          t1 = <GenericApprovalCard message={message} onApprove={onApprove} resolved={resolved} />;
          $[5] = message;
          $[6] = onApprove;
          $[7] = resolved;
          $[8] = t1;
        } else {
          t1 = $[8];
        }
        return t1;
      }
  }
};
const GenericApprovalCard = t0 => {
  const $ = _c(24);
  const {
    message,
    onApprove,
    resolved
  } = t0;
  const [answered, setAnswered] = useState(resolved ? "approved" : null);
  const {
    header,
    description
  } = message;
  const confirm_text = message.confirm_text ?? "Approve";
  const deny_text = message.deny_text ?? "Deny";
  let t1;
  if ($[0] !== message.approval_id || $[1] !== onApprove) {
    t1 = (approved, t2) => {
      const approveAll = t2 === undefined ? false : t2;
      onApprove?.(message.approval_id, approved, approveAll);
      setAnswered(approved ? "approved" : "denied");
    };
    $[0] = message.approval_id;
    $[1] = onApprove;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handle = t1;
  if (answered) {
    let t2;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <IcoShield16 className="shrink-0" />;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    const t3 = answered === "approved" ? "text-success" : "text-error";
    let t4;
    if ($[4] !== answered || $[5] !== t3) {
      t4 = <span className={t3}>{answered}</span>;
      $[4] = answered;
      $[5] = t3;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    let t5;
    if ($[7] !== header || $[8] !== t4) {
      t5 = <div className="flex items-center gap-2 text-caption-2 text-tertiary py-1">{t2}<span>{header} — {t4}</span></div>;
      $[7] = header;
      $[8] = t4;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    return t5;
  }
  let t2;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <IcoShield16 className="shrink-0 text-primary" />;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== header) {
    t3 = <div className="flex items-center gap-2">{t2}<span className="text-label-2 text-primary">{header}</span></div>;
    $[11] = header;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== description) {
    t4 = <p className="text-body-2 text-secondary">{description}</p>;
    $[13] = description;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== confirm_text || $[16] !== deny_text || $[17] !== handle || $[18] !== message.can_approve_all) {
    t5 = <ApprovalButtons confirmText={confirm_text} denyText={deny_text} canApproveAll={message.can_approve_all} onDecide={handle} />;
    $[15] = confirm_text;
    $[16] = deny_text;
    $[17] = handle;
    $[18] = message.can_approve_all;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== t3 || $[21] !== t4 || $[22] !== t5) {
    t6 = <div className="flex w-full min-w-0 flex-col gap-3 border border-tertiary rounded-lg p-4">{t3}{t4}{t5}</div>;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  return t6;
};

// `approved_brief` phase exists to give the user visual feedback that their approval was registered
type VendorApprovalPhase = 'pending' | 'approved_brief' | 'running' | 'completed' | 'denied';
const VendorApprovalCard = t0 => {
  const $ = _c(43);
  const {
    message,
    onApprove,
    toolResult,
    resolved
  } = t0;
  let t1;
  if ($[0] !== resolved || $[1] !== toolResult) {
    t1 = () => {
      if (resolved && toolResult) {
        return "completed";
      }
      if (resolved) {
        return "running";
      }
      return "pending";
    };
    $[0] = resolved;
    $[1] = toolResult;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const [phase, setPhase] = useState(t1);
  const {
    header,
    description
  } = message;
  const confirm_text = message.confirm_text ?? "Approve";
  const deny_text = message.deny_text ?? "Deny";
  let t2;
  let t3;
  if ($[3] !== phase) {
    t2 = () => {
      if (phase !== "approved_brief") {
        return;
      }
      const t = setTimeout(() => setPhase("running"), 2000);
      return () => clearTimeout(t);
    };
    t3 = [phase];
    $[3] = phase;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[6] !== phase || $[7] !== toolResult) {
    t4 = () => {
      if (toolResult && (phase === "running" || phase === "approved_brief")) {
        setPhase("completed");
      }
    };
    t5 = [toolResult, phase];
    $[6] = phase;
    $[7] = toolResult;
    $[8] = t4;
    $[9] = t5;
  } else {
    t4 = $[8];
    t5 = $[9];
  }
  useEffect(t4, t5);
  let t6;
  if ($[10] !== message.approval_id || $[11] !== onApprove) {
    t6 = (approved, t7) => {
      const approveAll = t7 === undefined ? false : t7;
      onApprove?.(message.approval_id, approved, approveAll);
      setPhase(approved ? "approved_brief" : "denied");
    };
    $[10] = message.approval_id;
    $[11] = onApprove;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const handle = t6;
  if (phase === "pending") {
    let t7;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <IcoShield16 className="shrink-0 text-primary" />;
      $[13] = t7;
    } else {
      t7 = $[13];
    }
    let t8;
    if ($[14] !== header) {
      t8 = <div className="flex items-center gap-2">{t7}<span className="text-label-2 text-primary">{header}</span></div>;
      $[14] = header;
      $[15] = t8;
    } else {
      t8 = $[15];
    }
    let t9;
    if ($[16] !== description) {
      t9 = <p className="text-body-2 text-secondary">{description}</p>;
      $[16] = description;
      $[17] = t9;
    } else {
      t9 = $[17];
    }
    let t10;
    if ($[18] !== confirm_text || $[19] !== deny_text || $[20] !== handle || $[21] !== message.can_approve_all) {
      t10 = <ApprovalButtons confirmText={confirm_text} denyText={deny_text} canApproveAll={message.can_approve_all} onDecide={handle} />;
      $[18] = confirm_text;
      $[19] = deny_text;
      $[20] = handle;
      $[21] = message.can_approve_all;
      $[22] = t10;
    } else {
      t10 = $[22];
    }
    let t11;
    if ($[23] !== t10 || $[24] !== t8 || $[25] !== t9) {
      t11 = <div className="flex w-full min-w-0 flex-col gap-3 border border-tertiary rounded-lg p-4">{t8}{t9}{t10}</div>;
      $[23] = t10;
      $[24] = t8;
      $[25] = t9;
      $[26] = t11;
    } else {
      t11 = $[26];
    }
    return t11;
  }
  if (phase === "approved_brief") {
    let t7;
    if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <IcoShield16 className="shrink-0" />;
      $[27] = t7;
    } else {
      t7 = $[27];
    }
    let t8;
    if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <span className="text-success">approved</span>;
      $[28] = t8;
    } else {
      t8 = $[28];
    }
    let t9;
    if ($[29] !== header) {
      t9 = <div className="flex items-center gap-2 text-caption-2 text-tertiary py-1">{t7}<span>{header} — {t8}</span></div>;
      $[29] = header;
      $[30] = t9;
    } else {
      t9 = $[30];
    }
    return t9;
  }
  if (phase === "denied") {
    let t7;
    if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <IcoShield16 className="shrink-0" />;
      $[31] = t7;
    } else {
      t7 = $[31];
    }
    let t8;
    if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <span className="text-error">denied</span>;
      $[32] = t8;
    } else {
      t8 = $[32];
    }
    let t9;
    if ($[33] !== header) {
      t9 = <div className="flex items-center gap-2 text-caption-2 text-tertiary py-1">{t7}<span>{header} — {t8}</span></div>;
      $[33] = header;
      $[34] = t9;
    } else {
      t9 = $[34];
    }
    return t9;
  }
  if (phase === "completed") {
    let t7;
    if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <span className="shrink-0 flex items-center h-[1.25em]"><IcoCheckCircle16 className="text-primary" /></span>;
      $[35] = t7;
    } else {
      t7 = $[35];
    }
    let t8;
    if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <span>Verification ran</span>;
      $[36] = t8;
    } else {
      t8 = $[36];
    }
    let t9;
    if ($[37] !== header) {
      t9 = <div className="flex items-start gap-1.5 text-caption-1 text-tertiary py-1">{t7}<div className="flex flex-col gap-1 min-w-0">{t8}<span className="font-mono truncate">{header}</span></div></div>;
      $[37] = header;
      $[38] = t9;
    } else {
      t9 = $[38];
    }
    return t9;
  }
  let t7;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <span className="shrink-0 flex items-center h-[1.25em]"><IcoShield16 /></span>;
    $[39] = t7;
  } else {
    t7 = $[39];
  }
  let t8;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <span>Running verification</span>;
    $[40] = t8;
  } else {
    t8 = $[40];
  }
  let t9;
  if ($[41] !== header) {
    t9 = <div className="flex items-start gap-1.5 text-caption-1 text-tertiary py-1">{t7}<div className="flex flex-col gap-1 min-w-0">{t8}<span className="font-mono truncate">{header}</span></div></div>;
    $[41] = header;
    $[42] = t9;
  } else {
    t9 = $[42];
  }
  return t9;
};
export type ToolDisplay = {
  icon: ReactNode;
  label: string;
  detail?: ReactNode;
  /** How to describe N invocations of this tool in a collapsed group header. */
  summarize?: (n: number) => string;
};
const plural = (n: number, singular: string, plural?: string) => `${n} ${n === 1 ? singular : plural ?? `${singular}s`}`;
const EntityToolDetail = t0 => {
  const $ = _c(8);
  const {
    fpId,
    extra
  } = t0;
  let t1;
  if ($[0] !== fpId) {
    t1 = fpId && <IdBadge id={fpId} size="compact" />;
    $[0] = fpId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== extra || $[3] !== fpId) {
    t2 = extra && <span>{fpId ? ` · ${extra}` : extra}</span>;
    $[2] = extra;
    $[3] = fpId;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <>{t1}{t2}</>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};

/** Show only the filename, never the internal `/workspace/...` path, for tool details. */
const basename = (p: unknown): string | undefined => typeof p === 'string' ? p.split('/').filter(Boolean).pop() ?? undefined : undefined;

/** A human-readable one-line summary of an `entities_query` call's filters — a name search
 * term when present, otherwise the active filters joined. Falls back to "All entities". */
const entitiesQueryDetail = (input: Record<string, unknown>): string => {
  const bsp = input.business_search_params as Record<string, unknown> | undefined;
  const psp = input.person_search_params as Record<string, unknown> | undefined;
  const name = bsp?.name as string || bsp?.dba as string || [psp?.first_name, psp?.last_name].filter(Boolean).join(' ');
  if (name?.trim()) return name.trim();
  const parts: string[] = [];
  if (typeof input.kind === 'string') parts.push(input.kind);
  if (input.requires_manual_review === true) parts.push('manual review');
  for (const key of ['statuses', 'labels', 'tags', 'risk_signal_reason_codes', 'custom_statuses']) {
    const arr = input[key];
    if (Array.isArray(arr) && arr.length > 0) parts.push(arr.map(String).join(', ').replace(/_/g, ' '));
  }
  return parts.length > 0 ? parts.join(' · ') : 'All entities';
};
const getSopToolIcon = (tool: SopMcpToolName): ReactNode => {
  switch (tool) {
    case 'mcp__sop__next':
      return <IcoArrowRightSmall16 />;
    case 'mcp__sop__definition':
      return <IcoFileText16 />;
    case 'mcp__sop__explain':
      return <IcoSearch16 />;
  }
};

/**
 * `input` is raw, untyped model output. This also runs in non-render contexts
 * (`hasVisibleContent` filters, `summarizeToolCalls`) that a React error boundary can't catch,
 * so a malformed payload that throws here would blank the whole panel. Swallow it to a generic
 * label instead; the per-case access below can stay lean.
 */
export const getToolDisplay = (tool: string, input: Record<string, unknown>): ToolDisplay | null => {
  try {
    return getToolDisplayInner(tool, input);
  } catch (err) {
    console.error('[agent-chat] getToolDisplay failed; falling back to generic label', tool, err);
    return {
      icon: <IcoLayer0116 />,
      label: 'Tool call'
    };
  }
};
const getToolDisplayInner = (tool: string, input: Record<string, unknown>): ToolDisplay | null => {
  if (isSopMcpToolName(tool)) {
    const metadata = getSopToolMetadata(tool);
    const rawDetail = metadata.detailInputKey ? input[metadata.detailInputKey] : undefined;
    const detail = typeof rawDetail === 'string' ? rawDetail : undefined;
    return {
      icon: getSopToolIcon(tool),
      label: metadata.label,
      detail,
      summarize: metadata.summarize
    };
  }

  // Agent-run polls coalesce into one quiet line in the chat (see chat-panel).
  if (isGetAgentRunTool(tool)) {
    return {
      icon: <IcoFileText16 />,
      label: 'Checking agent progress',
      summarize: n => n > 1 ? `checking agent progress (${n}×)` : 'checking agent progress'
    };
  }

  // Output IS a "… created" card; on success the reducer drops this row, so this label only shows
  // on the failed path (with an error glyph). No `summarize` — past tense would fight the glyph.
  if (isResourceCreationTool(tool)) {
    return {
      icon: <IcoFileText16 />,
      label: 'Creating resource'
    };
  }
  switch (tool) {
    // Internal plumbing — never shown.
    case 'ToolSearch':
      return null;
    case 'WebSearch':
      return {
        icon: <IcoSearch16 />,
        label: 'Searching the web',
        detail: input.query as string | undefined,
        summarize: n => n === 1 ? 'searched the web' : `ran ${n} web searches`
      };
    case 'WebFetch':
      return {
        icon: <IcoGlobe16 />,
        label: 'Fetching page',
        detail: input.url as string | undefined,
        summarize: n => `fetched ${plural(n, 'page')}`
      };
    case 'mcp__appearance__get_appearance':
      return {
        icon: <IcoPaint16 />,
        label: 'Reading the theme',
        summarize: () => 'read the theme'
      };
    case 'mcp__appearance__extract_brand':
      return {
        icon: <IcoGlobe16 />,
        label: 'Reading the brand',
        detail: input.source as string | undefined,
        summarize: n => n === 1 ? 'read the brand' : `read ${n} brand sources`
      };
    case 'mcp__appearance__apply_appearance':
      return {
        icon: <IcoSparkles16 />,
        label: 'Styling the theme',
        detail: input.narration as string | undefined,
        summarize: n => n === 1 ? 'styled the theme' : `styled the theme in ${n} steps`
      };
    case 'mcp__appearance__set_appearance_logo':
      return {
        icon: <IcoPaint16 />,
        label: 'Uploading the logo',
        detail: input.narration as string | undefined,
        summarize: () => 'uploaded the logo'
      };
    case 'mcp__meta__update_entity_vault':
      {
        const fpId = input.fp_id as string | undefined;
        const data = input.data as Record<string, unknown> | undefined;
        const fields = data ? Object.keys(data).sort().join(', ') : undefined;
        return {
          icon: <IcoPencil16 />,
          label: 'Updating vault',
          detail: <EntityToolDetail fpId={fpId} extra={fields} />,
          summarize: n => `updated ${plural(n, 'vault')}`
        };
      }
    case 'mcp__meta__upload_entity_file':
      {
        const fpId = input.fp_id as string | undefined;
        const identifier = input.identifier as string | undefined;
        return {
          icon: <IcoUpload16 />,
          label: 'Uploading file',
          detail: <EntityToolDetail fpId={fpId} extra={identifier} />,
          summarize: n => `uploaded ${plural(n, 'file')}`
        };
      }
    case 'mcp__meta__list_entity_vault':
      {
        const fpId = input.fp_id as string | undefined;
        return {
          icon: <IcoUnorderedList16 />,
          label: 'Listing vault fields',
          detail: fpId ? <IdBadge id={fpId} size="compact" /> : undefined,
          summarize: n => `listed ${plural(n, 'vault')}`
        };
      }
    case 'mcp__workspace__glob':
      return {
        icon: <IcoSearch16 />,
        label: 'Finding files',
        // The glob pattern (e.g. `**/*`) is internal noise — the label says enough.
        summarize: n => n === 1 ? 'found files' : `searched for files ${n} times`
      };
    case 'mcp__workspace__read':
      return {
        icon: <IcoFileText16 />,
        label: 'Reading',
        detail: basename(input.file_path),
        summarize: n => `read ${plural(n, 'file')}`
      };
    case 'mcp__workspace__grep':
      return {
        icon: <IcoSearch16 />,
        label: 'Searching',
        detail: input.pattern as string | undefined,
        summarize: n => `ran ${plural(n, 'search', 'searches')}`
      };
    case 'mcp__workspace__extract_text':
      {
        const files = input.files as {
          file_path: string;
        }[] | undefined ?? [];
        return {
          icon: <IcoFileText16 />,
          label: `Extracting text from ${plural(files.length, 'file')}`,
          detail: files.map(f => basename(f?.file_path)).filter(Boolean).join(', ') || undefined,
          summarize: n => `extracted text from ${plural(n, 'document')}`
        };
      }
    case 'mcp__workspace__bash':
    case 'Bash':
      {
        const command = input.command as string | undefined;
        if (command && isAgentBrowserCommand(input)) return agentBrowserDisplay(command);
        return {
          icon: <IcoCode16 />,
          label: 'Running command',
          detail: command ?? input.description as string | undefined,
          summarize: n => `ran ${plural(n, 'command')}`
        };
      }
    case 'mcp__workspace__view_image':
      return {
        icon: <IcoEye16 />,
        label: 'Viewing image',
        detail: basename(input.file_path),
        summarize: n => `viewed ${plural(n, 'image')}`
      };
    case 'mcp__workspace__write':
    case 'Write':
      return {
        icon: <IcoPencil16 />,
        label: 'Writing file',
        detail: basename(input.file_path),
        summarize: n => `wrote ${plural(n, 'file')}`
      };
    case 'mcp__workspace__edit':
    case 'Edit':
      return {
        icon: <IcoPencil16 />,
        label: 'Editing file',
        detail: basename(input.file_path),
        summarize: n => `edited ${plural(n, 'file')}`
      };
    case 'mcp__meta__entities_query':
      return {
        icon: <IcoLayer0116 />,
        label: 'Searching entities',
        detail: entitiesQueryDetail(input),
        summarize: n => n === 1 ? 'searched entities' : `ran ${n} entity searches`
      };
    case 'mcp__browser__start_browser':
      return {
        icon: <IcoGlobe16 />,
        label: 'Starting browser',
        summarize: () => 'started the browser'
      };
    case 'mcp__browser__wait_for_user':
      return {
        icon: <IcoGlobe16 />,
        label: 'Handing the browser to you',
        detail: input.message as string | undefined
      };
    case 'mcp__credentials__list_credentials':
      return {
        icon: <IcoGlobe16 />,
        label: 'Checking saved logins'
      };
    case 'mcp__credentials__list_credential_items':
      return {
        icon: <IcoGlobe16 />,
        label: 'Checking 1Password items'
      };
    case 'mcp__credentials__fill_credential':
      return {
        icon: <IcoGlobe16 />,
        label: 'Signing in with a saved login'
      };
    case 'mcp__credentials__request_credential':
      return {
        icon: <IcoGlobe16 />,
        label: 'Asking you for a login',
        detail: hostOf(input.origin) || undefined
      };
    // The subagent-spawn tool ("Agent" on current CLIs, "Task" on older ones). Journey
    // spawns render as a JourneyCard instead; this row covers other subagents and
    // sessions recorded before frame attribution existed.
    case 'Task':
    case 'Agent':
      return {
        icon: <IcoLayer0116 />,
        label: `Delegating to ${prettifySubagentType(input.subagent_type as string | undefined ?? '').toLowerCase()}`,
        summarize: n => `delegated ${plural(n, 'task')}`
      };
    default:
      {
        const pretty = tool.replace(/^mcp__[^_]+__/, '').replace(/_/g, ' ');
        const label = pretty.charAt(0).toUpperCase() + pretty.slice(1);
        return {
          icon: <IcoLayer0116 />,
          label
        };
      }
  }
};
const AGENT_BROWSER_LABELS: Record<string, string> = {
  snapshot: 'Reading the page',
  read: 'Reading the page',
  get: 'Reading the page',
  eval: 'Reading the page',
  find: 'Acting on the page',
  click: 'Clicking on the page',
  fill: 'Typing on the page',
  type: 'Typing on the page',
  press: 'Pressing a key',
  select: 'Choosing an option',
  check: 'Toggling a checkbox',
  uncheck: 'Toggling a checkbox',
  hover: 'Hovering on the page',
  scroll: 'Scrolling the page',
  scrollintoview: 'Scrolling the page',
  upload: 'Uploading a file',
  back: 'Going back',
  forward: 'Going forward',
  reload: 'Reloading the page',
  screenshot: 'Taking a screenshot',
  wait: 'Waiting for the page',
  tab: 'Switching tabs',
  frame: 'Switching frames',
  dialog: 'Handling a dialog'
};
const agentBrowserDisplay = (command: string): ToolDisplay => {
  const [, subcommand = '', rawTarget = ''] = command.match(/agent-browser\s+(\S+)\s*(\S*)/) ?? [];
  const target = rawTarget.replace(/^['"]|['"]$/g, '');
  if (subcommand === 'open') {
    const host = hostOf(target);
    return {
      icon: <IcoGlobe16 />,
      label: host ? `Opening ${host}` : 'Opening a page',
      detail: target || undefined
    };
  }
  return {
    icon: <IcoGlobe16 />,
    label: AGENT_BROWSER_LABELS[subcommand] ?? 'Working in the browser'
  };
};
const isSameResource = (a: ResourceContext, b: ResourceContext | undefined): boolean => {
  return !!b && a.kind === b.kind && resourceKey(a) === resourceKey(b);
};
export default ChatMessage;
function _temp() {
  return Date.now();
}
function _temp2(prev) {
  return !prev;
}
function _temp3(q) {
  return !!q.options && q.options.length > 0;
}
function _temp4(q_0) {
  return !!q_0.options && q_0.options.length > 0;
}
function _temp5(q_1) {
  return q_1.question;
}
function _temp6(e) {
  return !e;
}
function _temp7(value) {
  return !value;
}